import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {EMPTY, Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {Audit, AuditOwnerGroup, AuditType, AuditUtils, SMEApprover, AuditFilter, AuditHistory} from './audit.model';
import {API_URL} from '../app.constants';
import {RequestResult, TableData, Employee} from '../shared/shared.model';
import { isNumber } from 'util';

@Injectable()
export class AuditService {
  constructor(
    private http: HttpClient
  ) {}

  private auditFilter: AuditFilter;

  getAuditFilter(): AuditFilter {
    return this.auditFilter;
  }

  setAuditFilter(f: AuditFilter): void {
    this.auditFilter = f;
  }

  getAuditList(page: number = 0, pageSize: number = 10, searchKeyword: string = ''): Observable<TableData<Audit>> {
    if (this.auditFilter) {
      return this.http.post<TableData<Audit>>(`${API_URL}/AuditReports/flt`, this.auditFilter, {
        params: {
          SearchKeyword: searchKeyword,
          Page: (++page).toString(),
          PageSize: pageSize.toString(),
        },
      }).pipe(
        map((res: TableData<Audit>) => {
          res.page--;

          return res;
        })
      );
    } else {
      return this.http.get<TableData<Audit>>(`${API_URL}/AuditReports`, {
        params: {
          SearchKeyword: searchKeyword,
          Page: (++page).toString(),
          PageSize: pageSize.toString(),
        },
      }).pipe(
        map((res: TableData<Audit>) => {
          res.page--;

          return res;
        })
      );
    }
  }

  getAuditListHistory(page: number = 0, pageSize: number = 10, id: number = 0): Observable<TableData<AuditHistory>> {
    
    return this.http.get<TableData<AuditHistory>>(`${API_URL}/AuditReports/History/${id}`, {
      params: {          
        Page: (++page).toString(),
        PageSize: pageSize.toString(),
      },
    }).pipe(
      map((res: TableData<AuditHistory>) => {
        res.page--;

        return res;
      })
    );    
}

  exportXls(page: number = 0, pageSize: number = 10, searchKeyword: string = ''): Observable<Blob> {
    return this.http.post(`${API_URL}/AuditReports/export`, this.auditFilter, {
      params: {
        SearchKeyword: searchKeyword,
        Page: (++page).toString(),
        PageSize: pageSize.toString(),
      },
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      responseType: 'blob',
    });
  }

  getAudit(id: number | string): Observable<Audit> {
    return this.http.get<Audit>(`${API_URL}/AuditReports/${id}`);
  }  

  getAuditOwnerGroups(): Observable<Array<AuditOwnerGroup>> {
    return this.http.get<Array<AuditOwnerGroup>>(`${API_URL}/AuditOwnerGroups`);
  }

  getAuditTypes(): Observable<Array<AuditType>> {
    return this.http.get<Array<AuditType>>(`${API_URL}/AuditTypes`);
  }

  getSmeApprovers(auditOwnerGroupId: number): Observable<Array<SMEApprover>> {
    if (!isNumber(auditOwnerGroupId))
      return EMPTY;

    const params = {
      AuditOwnerGroupId: auditOwnerGroupId.toString(),
    };

    return this.http.get<Array<SMEApprover>>(`${API_URL}/SmeApprovers`, {params});
  }

  getEmployees(keyword: string) : Observable<Array<Employee>> {
    return this.http.get<Array<Employee>>(`${API_URL}/Employees/${keyword}`);
  }

  getSme(): Observable<Array<SMEApprover>> {
    return this.http.get<Array<SMEApprover>>(`${API_URL}/SmeApprovers/all`);
  }

  checkAuditExistence(name: string): Observable<boolean> {
    return this.http.get<boolean>(`${API_URL}/AuditReports/exists/${encodeURI(name)}`);
  }

  submitAudit(audit: Audit): Observable<number> {
    return this.http.post<number>(`${API_URL}/AuditReports`, AuditUtils.sanitize(audit));
  }
  updateAudit(audit: Audit): Observable<Audit> {
    return this.http.patch<Audit>(`${API_URL}/AuditReports/${audit.id}`, AuditUtils.sanitize(audit));
  }

  deleteAudit(id: number): Observable<RequestResult> {
    return this.http.delete<RequestResult>(`${API_URL}/AuditReports/${id}`);
  }
  submitEntireAudit(id: number): Observable<RequestResult> {
    return this.http.post<RequestResult>(`${API_URL}/AuditReports/${id}/submit`, undefined);
  }
}
